import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CategoryData } from '../../../../models/category-data.model';

@Component({
  selector: 'lib-progress-bar-card',
  standalone: false,
  templateUrl: './progress-bar-card.component.html',
  styleUrl: './progress-bar-card.component.scss',
})
export class ProgressBarCardComponent implements OnChanges {
  @Input() title!: string;
  @Input() number!: number;
  @Input() countIncrease?: number;
  @Input() percentage?: number;
  @Input() seeBreakdownVisible: boolean = false;
  @Input() categories: CategoryData[] = [];
  @Input() wide: boolean = false;

  @Output() detailsClicked = new EventEmitter<void>();

  maxNumberOfItemsInLegend = 3;
  adjustedPercentages: number[] = [];
  widthPercentages: number [] = [];
  sameLenght: boolean = true;

  get total(): number {
    return this.categories.reduce((sum, c) => sum + c.count, 0);
  }

  get visibleCategories(): CategoryData[] {
    return (this.categories ?? []).filter(
      (cat) => this.wide ? (cat.count / this.total) * 100 >= 0 : (cat.count / this.total) * 100 > 0.5
    );
  }

  get visibleTotal(): number {
    return this.visibleCategories.reduce((sum, cat) => sum + cat.count, 0);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['categories'] && this.categories?.length) {
      const visible = this.visibleCategories;
      this.adjustedPercentages = this.getAdjustedPercentages(visible, this.wide);
      this.widthPercentages = this.getWidthPercentages(visible.length)
    }
  }

  getWidthPercentages(categoriesCount: number): number[] {
    const widthPercentage = 100 / categoriesCount;
    const retArr: number[] = [];

    for (let i = 0; i < categoriesCount; i++) {
      retArr[i] = widthPercentage;
    }

    return retArr;
  }

  getAdjustedPercentages(categories: CategoryData[], isWide: boolean): number[] {
    const total = categories.reduce((sum, c) => sum + c.count, 0);

    if (total === 0) return categories.map(() => 0);

    const rawPercentages = categories.map((c) => (c.count / total) * 100);
    const floored = isWide ? rawPercentages.map(val => Math.floor(val * 100) / 100) : rawPercentages.map(Math.floor);
    const totalFloored = floored.reduce((sum, val) => sum + val, 0);
    const remainder = isWide ? Math.round((100 - totalFloored) * 100) / 100 : 100 - totalFloored;

    const remainders = rawPercentages.map((val, i) => ({
      index: i,
      decimal: val - floored[i],
    }));

    remainders.sort((a, b) => b.decimal - a.decimal);

    if (isWide)
      for (let i = 0; i < remainder * 100; i++) {
        floored[remainders[i].index] += 0.01;
      }
    else 
      for (let i = 0; i < remainder; i++) {
        floored[remainders[i].index]++;
      }

    return floored;
  }

  onCardClick(): void {
    if (this.seeBreakdownVisible) {
      this.detailsClicked.emit();
    }
  }

  get visibleLegendItems() {
    if (!this.wide) {
      const all = this.categories;
      return all.length > this.maxNumberOfItemsInLegend
        ? all.slice(0, this.maxNumberOfItemsInLegend)
        : all;
    } else return this.categories;
  }

  get shouldShowEllipsis() {
    return this.categories.length > this.maxNumberOfItemsInLegend && !this.wide;
  }

  get hasNonZeroCategories(): boolean {
    return this.categories?.some(cat => cat.count > 0) ?? false;
  }
}
